import type Luminary from '@lumen5/luminary';
import type { GetRootState } from 'js/src/libs/RootState';
import { VideoData } from '@lumen5/luminary';

import { getNonRecommendableAssetNames } from '../../video-state/serialization/assetSerializer';
import { getSceneDesignEditableDefaultText } from '../sceneDesignDefaults';
import type { LintFinding } from '../videoLinter';

import { getDisplayGroupNameForSceneDesign } from 'js/src/data/templates/templates-selectors';

/**
 * A quote design is only identifiable by its display group name — there is no content-type field
 * on SceneDesign, so this is the same signal the model keys off in the list_scene_designs catalog.
 * Group names are free-text per template, so match a `quote` prefix rather than the whole word to
 * cover plurals like "Quotes" or "Pull Quotes".
 */
function isQuoteDisplayGroup(groupName: string): boolean {
    return /\bquote/i.test(groupName);
}

/**
 * Warning: a quote design whose credit slots were left empty. A quote layout pairs the quote with
 * slots for who said it, so when the design ships two or more fillable text slots and the scene
 * uses only one, the speaker was dropped. The oversized decorative quotation glyph is a read-only
 * asset the agent cannot remove, so the result is a quotation mark stranded over copy nobody said
 * — which is how a quote design used for ordinary content looks broken.
 *
 * Counting slots keeps this language-independent: it asks what the design expects to be filled
 * rather than trying to recognize a name or a role in arbitrary author text. Designs that ship a
 * single text slot have nowhere to credit anyone and are left alone, and this stays a warning
 * because a scene can legitimately drop a role while keeping a name.
 *
 * Deliberately says nothing about quotation marks in the text: some designs carry the glyph as a
 * read-only asset and others type the marks into their own default text, so their presence or
 * absence in the author's text says nothing about whether the design was used correctly.
 */
export function checkQuoteDesignWithoutAttribution(
    luminary: Luminary,
    getState: GetRootState,
): LintFinding[] {
    const findings: LintFinding[] = [];
    const state = getState();

    luminary.scenes.forEach((scene) => {
        const groupName = getDisplayGroupNameForSceneDesign(state, scene.sceneDesign);
        if (!groupName || !isQuoteDisplayGroup(groupName)) {
            return;
        }

        const defaults = getSceneDesignEditableDefaultText(scene);
        if (!defaults) {
            return;
        }
        const nonRecommendable = getNonRecommendableAssetNames(scene, state);
        const slotsTheDesignExpects = [...defaults.keys()].filter(
            (name) => !nonRecommendable.has(name),
        );
        // Only one fillable text slot, so the design has nowhere to credit a speaker.
        if (slotsTheDesignExpects.length < 2) {
            return;
        }

        const filled = scene
            .getAssetsByType(VideoData.TEXT_ASSET_TYPE)
            .filter(
                (asset) =>
                    asset.isEditable && !nonRecommendable.has(asset.name) && asset.text?.trim(),
            );
        // Nothing written yet — the scene is still being built, not misused.
        if (filled.length !== 1) {
            return;
        }

        findings.push({
            check: 'quote_design_without_attribution',
            severity: 'warning',
            sceneId: scene.id,
            message: `This scene uses the quote scene design "${scene.sceneDesign}", which has ${slotsTheDesignExpects.length} text slots for a quote plus who said it, but only one is filled — nobody is credited. Double-check that this content really is a quotation. If it is, fill in the speaker's name and role; if it isn't, switch to a text/title design, because the design's decorative quotation mark usually can't be removed and looks wrong around ordinary copy.`,
        });
    });

    return findings;
}
